/**
 * Readers shared by the surface registries - the cells a column may be drawn with, the field types a
 * filter may collect its value through.
 *
 * Both are lists of entries naming what they render, the catalogue data types they suit, and the
 * capabilities the layout editor offers for them; these read that shape without knowing which list
 * they were handed.
 */

/** The default data type of a catalogue key that declares none. */
const DEFAULT_TYPE = 'text';

/**
 * What a configuration option decides: what is shown.
 *
 * Held here rather than beside the rest of the settings vocabulary because the registries declaring
 * settings are what that vocabulary reads, and a registry may not import what imports it.
 */
export const CONTENT = 'content';

/** What a configuration option decides: how what is shown is drawn. */
export const LAYOUT = 'layout';

/** What a configuration option decides: when what is shown is shown, and what it reads to decide. */
export const VISIBILITY = 'visibility';

/**
 * The entry a registry holds under a name.
 *
 * @param {Array} entries - The registry.
 * @param {String} idKey - What the registry calls an entry's name.
 * @param {String} id - The name to look up.
 *
 * @returns {Object|null}
 */
export const registryEntry = (entries, idKey, id) =>
  entries.find((entry) => entry[idKey] === id) ?? null;

/**
 * A capability an entry declares, as the flag the editor reads.
 *
 * @param {Array} entries - The registry.
 * @param {String} idKey - What the registry calls an entry's name.
 * @param {String} id - The name to look up.
 * @param {String} capability - The capability to read.
 *
 * @returns {Boolean}
 */
export const registryFlag = (entries, idKey, id, capability) =>
  Boolean(registryEntry(entries, idKey, id)?.[capability]);

/**
 * What an entry declares under a capability, for the ones carrying a list rather than a flag.
 *
 * @param {Array} entries - The registry.
 * @param {String} idKey - What the registry calls an entry's name.
 * @param {String} id - The name to look up.
 * @param {String} capability - The capability to read.
 *
 * @returns {Array}
 */
export const registryList = (entries, idKey, id, capability) =>
  registryEntry(entries, idKey, id)?.[capability] ?? [];

/**
 * The entries worth offering for a catalogue type, most specific first. An entry declaring no types
 * suits any key.
 *
 * @param {Array} entries - The registry.
 * @param {String} type - A catalogue entry's data type.
 *
 * @returns {Array}
 */
export const entriesForType = (entries, type) =>
  entries.filter(
    ({ types }) => types.length === 0 || types.includes(type ?? DEFAULT_TYPE)
  );
